// src/components/developer/UserManagementTab.js
// ✅ DROP-IN: This file now reads/writes ONLY `companyId` (no legacy variants anywhere)
// ✅ Seat capacity UI reads/writes ONLY: company.seatLimits.admin / company.seatLimits.endUser
// ✅ Preserves other company fields (merge:true)
// ✅ GiftSeatsPanel props fixed: company state + refreshCompany()
// ✅ Super admin access for roserobin13@gmail.com

import React, { useCallback, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import GiftSeatsPanel from "./GiftSeatsPanel";
import { postFunctionJson } from "../../utils/functionHttp";

const toDateInputValue = (value) => {
  if (!value) return "";
  let date = null;

  if (value instanceof Date) {
    date = value;
  } else if (value?.toDate) {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) date = parsed;
  } else if (typeof value === "number") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) date = parsed;
  }

  if (!date) return "";
  return date.toISOString().slice(0, 10);
};

const toInt = (v, fallback = 0) => {
  if (v === "" || v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : fallback;
};

// IMPORTANT: Do NOT default companyId to uid. Generate a proper company id if missing.
const generateCompanyId = () => doc(collection(db, "companies")).id;

// ✅ Check if current user is super admin
const isSuperAdmin = () => {
  const email = auth.currentUser?.email?.toLowerCase();
  return email === "roserobin13@gmail.com";
};

const UserManagementTab = () => {
  const [searchEmail, setSearchEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const [userDoc, setUserDoc] = useState(null);

  // Company (raw doc + editable form)
  const [company, setCompany] = useState(null); // used by GiftSeatsPanel
  const [companyForm, setCompanyForm] = useState(null);
  const [companyExists, setCompanyExists] = useState(false);

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  // Team + seats
  const [teamUsers, setTeamUsers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Groups management
  const [userGroups, setUserGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");

  const postSecure = useCallback(async (fnName, body) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");
    const authToken = await user.getIdToken(true);

    const res = await postFunctionJson(
      `https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/${fnName}`,
      {
        authToken,
        body: {
          authToken,
          ...body,
        },
      }
    );

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.error || `Failed to load ${fnName}`);
    }

    return data;
  }, []);

  // --------- helpers ----------
  const handleChangeCompanyField = (field, value) => {
    setCompanyForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  // ✅ STRICT: companyId only
  const resolveCompanyIdForOps = () => {
    const fromUser = (userDoc?.companyId || "").trim();
    const fromForm = (companyForm?.id || "").trim();
    return fromUser || fromForm || "";
  };

  const loadTeamUsers = async (companyId) => {
    if (!companyId) {
      setTeamUsers([]);
      return;
    }

    setLoadingTeam(true);
    try {
      console.log("[DevTools] Loading team users for companyId:", companyId);
      const qUsers = query(collection(db, "users"), where("companyId", "==", companyId));
      const snap = await getDocs(qUsers);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("[DevTools] Team users:", list);
      setTeamUsers(list);
    } catch (err) {
      console.error("[DevTools] Error loading team users:", err);
      setTeamUsers([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // --------- group management helpers ----------
  const loadUserGroups = async (userId, companyId) => {
    if (!userId || !companyId) {
      setUserGroups([]);
      setAvailableGroups([]);
      return;
    }

    setLoadingGroups(true);
    try {
      console.log("[DevTools] Loading groups for user:", userId, "company:", companyId);

      // companies/{companyId}/groups
      const groupsRef = collection(db, "companies", companyId, "groups");
      const groupsSnap = await getDocs(groupsRef);
      const allGroups = groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      console.log("[DevTools] All company groups:", allGroups);

      const groupsWithMembership = await Promise.all(
        allGroups.map(async (group) => {
          const memberRef = doc(db, "companies", companyId, "groups", group.id, "members", userId);
          const memberSnap = await getDoc(memberRef);
          return { ...group, isMember: memberSnap.exists() };
        })
      );

      setAvailableGroups(groupsWithMembership);
      setUserGroups(groupsWithMembership.filter((g) => g.isMember));
      console.log("[DevTools] Groups with membership status:", groupsWithMembership);
    } catch (err) {
      console.error("[DevTools] Error loading groups:", err);
      setUserGroups([]);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAddToGroup = async (groupId) => {
    if (!userDoc || !groupId) return;

    setStatus("");
    setError("");

    try {
      const companyId = resolveCompanyIdForOps();
      if (!companyId) throw new Error("Missing companyId. Set a valid companyId on the user or create a company first.");

      console.log("[DevTools] Adding user to group:", groupId, "company:", companyId);

      const memberRef = doc(db, "companies", companyId, "groups", groupId, "members", userDoc.id);
      await setDoc(
        memberRef,
        {
          uid: userDoc.id,
          role: userDoc.role || "owner",
          addedBy: auth.currentUser?.uid || userDoc.id,
          joinedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setStatus(`Added user to group: ${groupId}`);
      await loadUserGroups(userDoc.id, companyId);
    } catch (err) {
      console.error("[DevTools] Error adding to group:", err);
      setError(err.message || "Error adding user to group.");
    }
  };

  const handleRemoveFromGroup = async (groupId) => {
    if (!userDoc || !groupId) return;

    setStatus("");
    setError("");

    try {
      const companyId = resolveCompanyIdForOps();
      if (!companyId) throw new Error("Missing companyId. Set a valid companyId on the user or create a company first.");

      console.log("[DevTools] Removing user from group:", groupId, "company:", companyId);

      const memberRef = doc(db, "companies", companyId, "groups", groupId, "members", userDoc.id);
      await deleteDoc(memberRef);

      setStatus(`Removed user from group: ${groupId}`);
      await loadUserGroups(userDoc.id, companyId);
    } catch (err) {
      console.error("[DevTools] Error removing from group:", err);
      setError(err.message || "Error removing user from group.");
    }
  };

  // ✅ refreshCompany used by GiftSeatsPanel and after saves
  const refreshCompany = async (companyId) => {
    const cid = (companyId || "").trim();
    if (!cid) return;

    try {
      console.log("[DevTools] Refreshing company doc:", cid);
      const companyRef = doc(db, "companies", cid);
      const companySnap = await getDoc(companyRef);

      if (!companySnap.exists()) {
        console.warn("[DevTools] Company doc missing on refresh:", cid);
        setCompany(null);
        setCompanyExists(false);
        setCompanyForm((prev) =>
          prev
            ? {
                ...prev,
                id: cid,
                seatLimitsMissing: true,
              }
            : prev
        );
        return;
      }

      const cData = companySnap.data();
      setCompany(cData);

      const address = cData.address || {};
      const features = cData.features || {};
      const seatLimits = cData.seatLimits || {};

      const seatLimitsAdmin =
        typeof seatLimits.admin === "number" ? seatLimits.admin : seatLimits.admin ? Number(seatLimits.admin) : 0;
      const seatLimitsEndUser =
        typeof seatLimits.endUser === "number" ? seatLimits.endUser : seatLimits.endUser ? Number(seatLimits.endUser) : 0;

      setCompanyExists(true);
      setCompanyForm((prev) => ({
        ...(prev || {}),
        id: cid,
        businessName: cData.businessName || cData.name || "",
        businessType: cData.businessType || cData.companyType || "",
        companyEmail: cData.companyEmail || "",
        companyPhone: cData.companyPhone || "",
        addressStreet: address.street || "",
        addressCity: address.city || "",
        addressState: address.state || "",
        addressZip: address.zip || "",
        planType: cData.planType || "",
        stripeCustomerId: cData.stripeCustomerId || "",
        seatLimitsAdmin: Number.isFinite(seatLimitsAdmin) ? seatLimitsAdmin : 0,
        seatLimitsEndUser: Number.isFinite(seatLimitsEndUser) ? seatLimitsEndUser : 0,
        onboardingComplete: !!cData.onboardingComplete,
        trialEndsAtString: cData.trialEndsAt ? toDateInputValue(cData.trialEndsAt) : "",
        ownerUid: cData.ownerUid || userDoc?.id || "",
        notifyEndUsersLowStock: !!features.notifyEndUsersLowStock || !!cData.notifyEndUsersLowStock,
        requireNotificationClickToMarkRead:
          features.requireNotificationClickToMarkRead ?? cData.requireNotificationClickToMarkRead ?? true,
        seatLimitsMissing: !(cData.seatLimits && typeof cData.seatLimits === "object"),
      }));
    } catch (err) {
      console.error("[DevTools] refreshCompany error:", err);
      setError(err.message || "Error refreshing company.");
    }
  };

  // --------- search by email ----------
  const handleSearch = async (e) => {
    e.preventDefault();
    setStatus("");
    setError("");
    setUserDoc(null);
    setCompany(null);
    setCompanyForm(null);
    setCompanyExists(false);
    setTeamUsers([]);
    setUserGroups([]);
    setAvailableGroups([]);

    const email = searchEmail.trim().toLowerCase();
    if (!email) return;

    setLoading(true);
    try {
      console.log("[DevTools] Searching user by email:", email);

      const result = await postSecure("queryUserByEmailSecure", { email });
      const user = result?.user || null;

      if (!user) {
        setError("No user found with that email.");
        console.warn("[DevTools] No user found for email:", email);
        return;
      }

      const userTrialEndsAtString = toDateInputValue(user.trialEndsAt);

      const userWithHelpers = {
        ...user,
        trialEndsAtString: userTrialEndsAtString,
      };

      console.log("[DevTools] Found user:", userWithHelpers);
      setUserDoc(userWithHelpers);

      // ✅ STRICT: companyId only
      const rawCompanyId = (user.companyId || "").trim();

      // ✅ IMPORTANT: Use a local variable so we can load team/groups even if we just generated an id
      let companyIdForLoads = rawCompanyId;

      // --- load or prepare company doc ---
      if (rawCompanyId) {
        console.log("[DevTools] Loading company doc:", rawCompanyId);
        const companyRef = doc(db, "companies", rawCompanyId);
        const companySnap = await getDoc(companyRef);

        if (companySnap.exists()) {
          const cData = companySnap.data();
          setCompany(cData);

          console.log("[DevTools] Loaded company:", cData);

          const address = cData.address || {};
          const features = cData.features || {};
          const seatLimits = cData.seatLimits || {};

          const seatLimitsAdmin =
            typeof seatLimits.admin === "number" ? seatLimits.admin : seatLimits.admin ? Number(seatLimits.admin) : 0;
          const seatLimitsEndUser =
            typeof seatLimits.endUser === "number" ? seatLimits.endUser : seatLimits.endUser ? Number(seatLimits.endUser) : 0;

          setCompanyExists(true);
          setCompanyForm({
            id: rawCompanyId,
            businessName: cData.businessName || cData.name || "",
            businessType: cData.businessType || cData.companyType || "",
            companyEmail: cData.companyEmail || "",
            companyPhone: cData.companyPhone || "",
            addressStreet: address.street || "",
            addressCity: address.city || "",
            addressState: address.state || "",
            addressZip: address.zip || "",
            planType: cData.planType || "",
            stripeCustomerId: cData.stripeCustomerId || "",
            seatLimitsAdmin: Number.isFinite(seatLimitsAdmin) ? seatLimitsAdmin : 0,
            seatLimitsEndUser: Number.isFinite(seatLimitsEndUser) ? seatLimitsEndUser : 0,
            onboardingComplete: !!cData.onboardingComplete,
            trialEndsAtString: cData.trialEndsAt ? toDateInputValue(cData.trialEndsAt) : "",
            ownerUid: cData.ownerUid || user.id,
            notifyEndUsersLowStock: !!features.notifyEndUsersLowStock || !!cData.notifyEndUsersLowStock,
            requireNotificationClickToMarkRead:
              features.requireNotificationClickToMarkRead ?? cData.requireNotificationClickToMarkRead ?? true,
            seatLimitsMissing: !(cData.seatLimits && typeof cData.seatLimits === "object"),
          });
        } else {
          console.warn("[DevTools] Company doc is missing:", rawCompanyId);
          setCompany(null);

          setCompanyExists(false);
          setCompanyForm({
            id: rawCompanyId,
            businessName: "",
            businessType: "",
            companyEmail: user.email || "",
            companyPhone: "",
            addressStreet: "",
            addressCity: "",
            addressState: "",
            addressZip: "",
            planType: "trial",
            stripeCustomerId: "",
            seatLimitsAdmin: 1,
            seatLimitsEndUser: 1,
            onboardingComplete: false,
            trialEndsAtString: "",
            ownerUid: user.id,
            notifyEndUsersLowStock: false,
            requireNotificationClickToMarkRead: true,
            seatLimitsMissing: true,
          });
        }
      } else {
        console.warn("[DevTools] User has no companyId, preparing new company.");
        const newCompanyId = generateCompanyId();
        companyIdForLoads = newCompanyId;
        setCompany(null);

        setCompanyExists(false);
        setCompanyForm({
          id: newCompanyId,
          businessName: "",
          businessType: "",
          companyEmail: user.email || "",
          companyPhone: "",
          addressStreet: "",
          addressCity: "",
          addressState: "",
          addressZip: "",
          planType: "trial",
          stripeCustomerId: "",
          seatLimitsAdmin: 1,
          seatLimitsEndUser: 1,
          onboardingComplete: false,
          trialEndsAtString: "",
          ownerUid: user.id,
          notifyEndUsersLowStock: false,
          requireNotificationClickToMarkRead: true,
          seatLimitsMissing: true,
        });

        // Prefill the user's companyId locally (saved only when you click "Save user" or "Save company")
        setUserDoc((prev) =>
          prev
            ? {
                ...prev,
                companyId: newCompanyId,
              }
            : prev
        );
      }

      await loadTeamUsers(companyIdForLoads);
      await loadUserGroups(user.id, companyIdForLoads);

      setStatus("Loaded user and company data.");
    } catch (err) {
      console.error("[DevTools] Search error:", err);
      setError(err.message || "Error searching user.");
    } finally {
      setLoading(false);
    }
  };

  // --------- save user doc ----------
  const handleSaveUser = async () => {
    if (!userDoc) return;
    setStatus("");
    setError("");

    try {
      const companyId = resolveCompanyIdForOps();
      const userRef = doc(db, "users", userDoc.id);

      const payload = { ...userDoc };
      delete payload.id;
      delete payload.trialEndsAtString;

      if (userDoc.trialEndsAtString) {
        const d = new Date(userDoc.trialEndsAtString);
        if (!isNaN(d.getTime())) payload.trialEndsAt = Timestamp.fromDate(d);
      }

      if (companyId) payload.companyId = companyId;
      payload.updatedAt = serverTimestamp();

      console.log("[DevTools] Saving user doc:", userRef.path, payload);
      await setDoc(userRef, payload, { merge: true });

      setStatus("User document saved.");
    } catch (err) {
      console.error("[DevTools] Save user error:", err);
      setError(err.message || "Error saving user.");
    }
  };

  // --------- save / create company doc ----------
  const handleSaveCompany = async () => {
    if (!companyForm) return;
    setStatus("");
    setError("");

    try {
      const companyId = (companyForm.id || "").trim();
      if (!companyId) throw new Error("Missing companyForm.id");

      const companyRef = doc(db, "companies", companyId);

      const trialDate = companyForm.trialEndsAtString
        ? Timestamp.fromDate(new Date(companyForm.trialEndsAtString))
        : null;

      const seatLimitsAdmin = toInt(companyForm.seatLimitsAdmin, 0);
      const seatLimitsEndUser = toInt(companyForm.seatLimitsEndUser, 0);

      const payload = {
        businessName: companyForm.businessName || "",
        businessType: companyForm.businessType || "",
        companyEmail: companyForm.companyEmail || "",
        companyPhone: companyForm.companyPhone || "",
        address: {
          street: companyForm.addressStreet || "",
          city: companyForm.addressCity || "",
          state: companyForm.addressState || "",
          zip: companyForm.addressZip || "",
        },
        ownerUid: companyForm.ownerUid || userDoc?.id,
        planType: companyForm.planType || "trial",
        stripeCustomerId: companyForm.stripeCustomerId || null,
        onboardingComplete: !!companyForm.onboardingComplete,
        trialEndsAt: trialDate || null,

        // ✅ Seats: ONLY seatLimits
        seatLimits: {
          admin: seatLimitsAdmin,
          endUser: seatLimitsEndUser,
        },

        features: {
          notifyEndUsersLowStock: !!companyForm.notifyEndUsersLowStock,
          requireNotificationClickToMarkRead: !!companyForm.requireNotificationClickToMarkRead,
        },

        // optional (harmless): keeps a self-reference using the SAME field name
        companyId,

        updatedAt: serverTimestamp(),
      };

      console.log("[DevTools] Saving company doc:", companyRef.path, payload);
      await setDoc(companyRef, payload, { merge: true });

      // ✅ If user isn't linked (or was missing), link them now
      if (userDoc?.id) {
        const userRef = doc(db, "users", userDoc.id);
        const currentCompanyId = (userDoc.companyId || "").trim();

        if (currentCompanyId !== companyId) {
          console.log("[DevTools] Linking user to companyId:", companyId);
          await setDoc(
            userRef,
            {
              companyId,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setUserDoc((prev) => (prev ? { ...prev, companyId } : prev));
        }
      }

      // ✅ Refresh raw company + form so GiftSeatsPanel has the latest doc
      await refreshCompany(companyId);

      setStatus("Company document saved (seatLimits updated).");

      // Refresh derived sections
      await loadTeamUsers(companyId);
      if (userDoc?.id) await loadUserGroups(userDoc.id, companyId);
    } catch (err) {
      console.error("[DevTools] Save company error:", err);
      setError(err.message || "Error saving company.");
    }
  };

  // --------- derived stats for seat / user overview ----------
  const activeTeam = teamUsers.filter((u) => (u.status || "Active") !== "Deleted");

  const adminCount = activeTeam.filter((u) => u.role === "admin" || u.role === "owner").length;
  const endUserCount = activeTeam.filter((u) => u.role === "endUser").length;
  const customerCount = activeTeam.filter((u) => u.role === "customer").length;

  const adminLimit = companyForm ? toInt(companyForm.seatLimitsAdmin, 0) : 0;
  const endUserLimit = companyForm ? toInt(companyForm.seatLimitsEndUser, 0) : 0;

  const adminOver = adminLimit > 0 && adminCount > adminLimit;
  const endUserOver = endUserLimit > 0 && endUserCount > endUserLimit;

  const companyForGiftSeats = company || null;

  const showSuperAdminBadge = isSuperAdmin();

  return (
    <div className="space-y-4">
      {/* Search */}
      <form
        onSubmit={handleSearch}
        className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row gap-2 items-center"
      >
        <input
          type="email"
          required
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          placeholder="Search user by email…"
          className="flex-1 px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-60"
        >
          {loading ? "Searching…" : "Load user"}
        </button>
        {showSuperAdminBadge && (
          <span className="text-[10px] px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
            🔑 Super Admin Access
          </span>
        )}
      </form>

      {status && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
          {status}
        </div>
      )}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* User + company forms */}
      {userDoc && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* LEFT COLUMN: User card + Seat & user overview + Groups */}
          <div className="space-y-3">
            {/* User card */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">User</h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  uid: {userDoc.id}
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div>
                  <label className="block text-[11px] text-gray-500">Email</label>
                  <input
                    type="email"
                    value={userDoc.email || ""}
                    onChange={(e) => setUserDoc((prev) => ({ ...prev, email: e.target.value }))}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500">Role</label>
                    <select
                      value={userDoc.role || ""}
                      onChange={(e) => setUserDoc((prev) => ({ ...prev, role: e.target.value }))}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    >
                      <option value="">(none)</option>
                      <option value="owner">owner</option>
                      <option value="admin">admin</option>
                      <option value="endUser">endUser</option>
                      <option value="customer">customer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500">Company ID</label>
                    <input
                      type="text"
                      value={resolveCompanyIdForOps()}
                      onChange={(e) => setUserDoc((prev) => ({ ...prev, companyId: e.target.value }))}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                  </div>
                </div>

                {/* User-level plan & subscription flags */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500">User planType</label>
                    <select
                      value={userDoc.planType || ""}
                      onChange={(e) => setUserDoc((prev) => ({ ...prev, planType: e.target.value }))}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    >
                      <option value="">(none)</option>
                      <option value="trial">trial</option>
                      <option value="paid">paid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500">subscriptionActive</label>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="font-mono text-[11px] text-gray-700">
                        {String(userDoc.subscriptionActive ?? false)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setUserDoc((prev) => ({ ...prev, subscriptionActive: !prev.subscriptionActive }))}
                        className="px-2 py-0.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-[11px]"
                      >
                        Toggle
                      </button>
                    </div>
                  </div>
                </div>

                {/* User-level trialEndsAt */}
                <div>
                  <label className="block text-[11px] text-gray-500">trialEndsAt (user) – YYYY-MM-DD</label>
                  <input
                    type="date"
                    value={userDoc.trialEndsAtString || ""}
                    onChange={(e) => setUserDoc((prev) => ({ ...prev, trialEndsAtString: e.target.value }))}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                {/* setupCompleted + companySetupCompleted */}
                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-gray-500">
                    setupCompleted:{" "}
                    <span className="font-mono">{String(userDoc.setupCompleted ?? false)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUserDoc((prev) => ({ ...prev, setupCompleted: !prev.setupCompleted }))}
                    className="text-[11px] text-blue-600 hover:text-blue-700"
                  >
                    Toggle
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-gray-500">
                    companySetupCompleted:{" "}
                    <span className="font-mono">{String(userDoc.companySetupCompleted ?? false)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setUserDoc((prev) => ({ ...prev, companySetupCompleted: !prev.companySetupCompleted }))
                    }
                    className="text-[11px] text-blue-600 hover:text-blue-700"
                  >
                    Toggle
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSaveUser}
                className="mt-2 w-full px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
              >
                Save user
              </button>
            </div>

            {/* Group membership management */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Group Memberships</h3>
                {loadingGroups && <span className="text-[10px] text-gray-500">Loading...</span>}
              </div>

              {/* Current groups */}
              <div className="space-y-2">
                <label className="block text-[11px] text-gray-500">Current groups ({userGroups.length})</label>
                {userGroups.length === 0 ? (
                  <div className="text-[11px] text-gray-400 italic">Not a member of any groups</div>
                ) : (
                  <div className="space-y-1">
                    {userGroups.map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center justify-between p-2 rounded-md bg-blue-50 border border-blue-100"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-medium text-gray-900 truncate">{group.name || group.id}</div>
                          <div className="text-[10px] text-gray-500 font-mono truncate">{group.id}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFromGroup(group.id)}
                          className="ml-2 px-2 py-0.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 text-[10px] font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add to group */}
              <div className="space-y-2 pt-2 border-t border-gray-200">
                <label className="block text-[11px] text-gray-500">Add to group</label>
                <div className="flex gap-2">
                  <select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  >
                    <option value="">Select a group...</option>
                    {availableGroups
                      .filter((g) => !g.isMember)
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name || group.id}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedGroupId) {
                        handleAddToGroup(selectedGroupId);
                        setSelectedGroupId("");
                      }
                    }}
                    disabled={!selectedGroupId}
                    className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Available groups overview */}
              <div className="pt-2 border-t border-gray-200">
                <div className="text-[11px] text-gray-500">Available groups: {availableGroups.length}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {availableGroups.map((group) => (
                    <span
                      key={group.id}
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] ${
                        group.isMember ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {group.name || group.id}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Seat & user overview – under User section */}
            {companyForm && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-800">Seat &amp; user overview</div>
                  {loadingTeam && <span className="text-[11px] text-slate-500">Loading team…</span>}
                </div>

                {companyForm.seatLimitsMissing && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                    ⚠️ This company has no <span className="font-mono">seatLimits</span> yet. Set limits below and click{" "}
                    <span className="font-semibold">Save company</span>.
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <div className="text-[11px] text-slate-500">Admins / owners</div>
                    <div className={`font-mono ${adminOver ? "text-red-700" : "text-slate-900"}`}>
                      {adminCount} / {adminLimit}
                    </div>
                    {adminOver && (
                      <div className="text-[10px] text-red-600 mt-0.5">
                        Over limit by {adminCount - adminLimit}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-[11px] text-slate-500">End users</div>
                    <div className={`font-mono ${endUserOver ? "text-red-700" : "text-slate-900"}`}>
                      {endUserCount} / {endUserLimit}
                    </div>
                    {endUserOver && (
                      <div className="text-[10px] text-red-600 mt-0.5">
                        Over limit by {endUserCount - endUserLimit}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-[11px] text-slate-500">Customers</div>
                    <div className="font-mono text-slate-900">{customerCount}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">(not seat-limited)</div>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-600">
                  Capacity is controlled by <span className="font-mono">company.seatLimits</span> only.
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Company card */}
          {companyForm && (
            <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Company {companyExists ? "" : "(will create)"}
                </h2>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  companyId: {companyForm.id}
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div>
                  <label className="block text-[11px] text-gray-500">Business name</label>
                  <input
                    type="text"
                    value={companyForm.businessName}
                    onChange={(e) => handleChangeCompanyField("businessName", e.target.value)}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500">Business type</label>
                    <input
                      type="text"
                      value={companyForm.businessType}
                      onChange={(e) => handleChangeCompanyField("businessType", e.target.value)}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500">Plan type</label>
                    <select
                      value={companyForm.planType}
                      onChange={(e) => handleChangeCompanyField("planType", e.target.value)}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    >
                      <option value="">(none)</option>
                      <option value="trial">trial</option>
                      <option value="paid">paid</option>
                    </select>
                  </div>
                </div>

                {/* ✅ Seat limits ONLY */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500">Admin seat limit</label>
                    <input
                      type="number"
                      min={0}
                      value={companyForm.seatLimitsAdmin}
                      onChange={(e) =>
                        handleChangeCompanyField(
                          "seatLimitsAdmin",
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500">End-user seat limit</label>
                    <input
                      type="number"
                      min={0}
                      value={companyForm.seatLimitsEndUser}
                      onChange={(e) =>
                        handleChangeCompanyField(
                          "seatLimitsEndUser",
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                  </div>
                </div>

                {/* 🎁 Gift seats */}
                <div className="pt-3 border-t border-gray-200">
                  <GiftSeatsPanel
                    companyId={companyForm.id}
                    company={companyForGiftSeats}
                    onRefreshCompany={() => refreshCompany(companyForm.id)}
                  />
                </div>

                {/* Company-level trialEndsAt (still useful) */}
                <div>
                  <label className="block text-[11px] text-gray-500">trialEndsAt (company) – YYYY-MM-DD</label>
                  <input
                    type="date"
                    value={companyForm.trialEndsAtString || ""}
                    onChange={(e) => handleChangeCompanyField("trialEndsAtString", e.target.value)}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-500">Company email</label>
                  <input
                    type="email"
                    value={companyForm.companyEmail}
                    onChange={(e) => handleChangeCompanyField("companyEmail", e.target.value)}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-gray-500">Company phone</label>
                  <input
                    type="text"
                    value={companyForm.companyPhone}
                    onChange={(e) => handleChangeCompanyField("companyPhone", e.target.value)}
                    className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                {/* Address */}
                <div className="grid grid-cols-1 gap-1">
                  <label className="block text-[11px] text-gray-500">Address</label>
                  <input
                    type="text"
                    placeholder="Street"
                    value={companyForm.addressStreet}
                    onChange={(e) => handleChangeCompanyField("addressStreet", e.target.value)}
                    className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="City"
                      value={companyForm.addressCity}
                      onChange={(e) => handleChangeCompanyField("addressCity", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={companyForm.addressState}
                      onChange={(e) => handleChangeCompanyField("addressState", e.target.value)}
                      className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Zip"
                    value={companyForm.addressZip}
                    onChange={(e) => handleChangeCompanyField("addressZip", e.target.value)}
                    className="w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500">Stripe customer ID</label>
                    <input
                      type="text"
                      value={companyForm.stripeCustomerId}
                      onChange={(e) => handleChangeCompanyField("stripeCustomerId", e.target.value)}
                      className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Feature flags */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-500">Notify end users on low stock</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleChangeCompanyField("notifyEndUsersLowStock", !companyForm.notifyEndUsersLowStock)
                      }
                      className="px-2 py-0.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      {companyForm.notifyEndUsersLowStock ? "On" : "Off"}
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-500">Require click to mark notifications read</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleChangeCompanyField(
                          "requireNotificationClickToMarkRead",
                          !companyForm.requireNotificationClickToMarkRead
                        )
                      }
                      className="px-2 py-0.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      {companyForm.requireNotificationClickToMarkRead ? "On" : "Off"}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-gray-500">
                    onboardingComplete: <span className="font-mono">{String(companyForm.onboardingComplete)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChangeCompanyField("onboardingComplete", !companyForm.onboardingComplete)}
                    className="text-[11px] text-blue-600 hover:text-blue-700"
                  >
                    Toggle
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSaveCompany}
                className="mt-2 w-full px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
              >
                {companyExists ? "Save company" : "Create / save company"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
